import React, { useState, useContext, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import styled from 'styled-components'
import { BsCheck2Circle } from 'react-icons/bs'
import { SlQuestion } from 'react-icons/sl'
import toast from 'react-hot-toast'
import Joi from 'joi'

import { ModalContext } from '../../../context/ModalContext'

import Modal from '../../../components/Modal'
import RichTextbox from '../../../components/RichTextbox'

import apiClient from '../../../helpers/apiClient'

import { setJobsUpdated } from '../../../redux/feature/jobSlice'

import { getFromLocalStorage } from '../../../utils/util'

const WithdrawApplicationModal = () => {
  const dispatch = useDispatch()
  const applicantCvDetails = getFromLocalStorage('applicant-cv-data')
  const applicantId = applicantCvDetails?.applicant_id

  const [loading, setLoading] = useState(false)
  const [isWithdrawn, setIsWithdrawn] = useState(false)
  const [reason, setReason] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})

  const { setModal } = useContext(ModalContext)

  const validationSchema = useMemo(() => {
    return Joi.object({
      job_id: Joi.string().label('Job ID').required(),
      reason: Joi.string().label('Reason').required(),
    })
  }, [])

  const selectedJob = useSelector((state) => state.jobs.selectedJob)

  const handleConfirmWithdraw = () => {
    const payload = {
      job_id: selectedJob?.jobId.toString(),
      reason: reason,
    }

    const result = validationSchema.validate(payload, { abortEarly: false })

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please provide a reason for withdrawal before proceeding.')

      return
    }

    setLoading(true)
    apiClient
      .put(`/applicant/${applicantId}/withdraw`, payload)
      .then(() => {
        setIsWithdrawn(true) // static modal to show success message
        dispatch(setJobsUpdated(true))
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message)
      })
      .finally(() => {
        setReason('')
        setLoading(false)
      })
  }

  const handleCloseModal = () => {
    setModal(false)
    setIsWithdrawn(false)
    setReason('')
    setFieldErrors({})
  }

  return (
    <>
      {!isWithdrawn ? (
        <Modal
          btnTitle={loading ? 'Loading...' : 'Continue'}
          btnFunc={handleConfirmWithdraw}
        >
          <StyledConfirmApplication>
            <SlQuestion className="question_icon" />
            <h1>Confirm Application Withdrawal</h1>
            <p>
              Clicking the Continue button will confirm your withdrawal on this
              application.
            </p>
            <br />
            <br />

            <label htmlFor="reason">
              Please provide a reason for withdrawal.
            </label>
            <RichTextbox
              id="reason"
              buttons={['undo', 'redo']}
              className={'reason' in fieldErrors && 'has-error'}
              errorMessage={fieldErrors?.reason}
              content={reason}
              setContent={setReason}
            />
          </StyledConfirmApplication>
        </Modal>
      ) : (
        <Modal
          btnTitle="Finish"
          onOverlayClick={handleCloseModal}
          btnFunc={handleCloseModal}
          closeBtnFunc={handleCloseModal}
          noCancel
        >
          <StyledSuccessfulApplication>
            <BsCheck2Circle className="success_icon" />
            <h1>Application Withdrawal Successful!</h1>
            <br />
            <p>
              You have successfully withdrawn your application. We wish you the
              best in your job search!
            </p>
          </StyledSuccessfulApplication>
        </Modal>
      )}
    </>
  )
}

export default WithdrawApplicationModal

const StyledConfirmApplication = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  align-items: center;
  justify-content: center;

  .question_icon {
    font-size: 5rem;
    color: #ffa500;
    margin: 0.5rem;
  }
`

const StyledSuccessfulApplication = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .success_icon {
    font-size: 5rem;
    color: #5cca95;
    margin: 0.5rem;
  }

  h1,
  p {
    text-align: center;
  }
`
